import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import elecIcon from './imgs/elec_icon.png';
import pvIcon from './imgs/pv_icon.png';
import waterIcon from './imgs/water_icon.png';
import gasIcon from './imgs/gas_icon.png';

export enum ReportType {
  DAILY = 'daily',
  MONTHLY = 'monthly',
}

export const REPORT_TYPE_LABELS = {
  [ReportType.DAILY]: '日报',
  [ReportType.MONTHLY]: '月报',
};

export const ENERGY_TYPES = [
  { key: 'electricity', name: '电能', icon: elecIcon, unit: 'kWh', color: '#00ADFF' },
  { key: 'solar', name: '光伏发电', icon: pvIcon, unit: 'kWh', color: '#FFB800' },
  { key: 'tapWater', name: '自来水', icon: waterIcon, unit: 'm³', color: '#4FC3F7' },
  { key: 'reclaimedWater', name: '中水', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'softWater', name: '软水', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'newWater', name: '新水', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'liquidArgon', name: '液氩', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'liquidOxygen', name: '液氧', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'liquidNitrogen', name: '液氮', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'drinkingWater', name: '饮用水', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'drainage', name: '下水/污水', icon: waterIcon, unit: 'm³', color: '#29B6F6' },
  { key: 'compressedAir', name: '压缩空气', icon: gasIcon, unit: 'Nm³', color: '#9575CD' },
  { key: 'oxygen', name: '氧气', icon: gasIcon, unit: 'Nm³', color: '#4DB6AC' },
  { key: 'naturalGas', name: '天然气', icon: gasIcon, unit: 'Nm³', color: '#FF8A65' },
  { key: 'carbonDioxide', name: '二氧化碳', icon: gasIcon, unit: 'Nm³', color: '#AED581' },
  { key: 'hydrogen', name: '氢气', icon: gasIcon, unit: 'Nm³', color: '#E57373' },
];

export const getDisabledDate = (reportType: ReportType) => {
  const today = dayjs();
  if (reportType === ReportType.DAILY) {
    // 日报：只能选择昨日及之前
    return (current: dayjs.Dayjs) => current && current >= today.startOf('day');
  }
  // 月报：只能选择本月及之前的月份（但本月数据不含今日）
  return (current: dayjs.Dayjs) => current && current > today.startOf('month');
};

export const getDefaultDate = (reportType: ReportType) => {
  const today = dayjs();
  if (reportType === ReportType.DAILY) {
    // 日报默认昨日
    return today.subtract(1, 'day');
  }
  // 月报默认本月
  return today.startOf('month');
};

/**
 * 计算报告时间范围
 * @param reportType 报告类型
 * @param selectedDate 选定的日期
 * @returns { fromTime, toTime, timeResolution }
 */
export const getReportTimeRange = (reportType: ReportType, selectedDate: Dayjs) => {
  const today = dayjs();
  const timeResolution: 'DAY' | 'MONTH' = reportType === ReportType.DAILY ? 'DAY' : 'MONTH';

  if (reportType === ReportType.DAILY) {
    // 日报：选定日期的当天
    const fromTime = selectedDate.startOf('day').valueOf();
    const toTime = selectedDate.endOf('day').valueOf();
    return { fromTime, toTime, timeResolution };
  } else {
    // 月报：选定月份的数据
    const fromTime = selectedDate.startOf('month').valueOf();
    // 如果是本月，数据不含今日，到昨天为止
    const isCurrentMonth = selectedDate.month() === today.month() && selectedDate.year() === today.year();
    const toTime = isCurrentMonth
      ? today.subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf('month').valueOf();
    return { fromTime, toTime, timeResolution };
  }
};
